import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { ApolloClient, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getOperationDefinition } from '@apollo/client/utilities';
import * as Duration from '@effect/data/Duration';
import { whenW } from '@infl-ts/util-function';
import { fetchHubJwtToken } from '@infl-ts/util-hub-axios-instance';
import { attachScheme } from '@infl-ts/util-url';
import { constVoid, flow, identity, pipe } from 'fp-ts/function';
import * as io from 'fp-ts/IO';
import * as Pred from 'fp-ts/Predicate';
import * as RA from 'fp-ts/ReadonlyArray';
import * as S from 'fp-ts/string';
import * as TE from 'fp-ts/TaskEither';
import { createClient } from 'graphql-ws';
import { useEffect, useMemo, useState } from 'react';
import WS from 'websocket';
import { constructCache } from './construct-cache';
import { concat, split } from './utils';

var isUnauthenticatedError = function isUnauthenticatedError(error) {
  return error.name === 'ServerError' && 'statusCode' in error && error.statusCode === 401;
};

var isGraphQLUnauthenticatedError = function isGraphQLUnauthenticatedError(err, operationName) {
  return (err.message.toLowerCase().startsWith('authentication is required') || err.message.toLowerCase().startsWith('authentication required')) && 'GAProfileId' !== operationName;
};

var isAuthError = pipe(function (e) {
  return !!e.networkError && isUnauthenticatedError(e.networkError);
}, Pred.or(function (e) {
  return !!e.graphQLErrors && pipe(e.graphQLErrors, // since our data fetching layer does not deal with partial data, we are
  // going to fail when we get at least one unauthenticated error
  RA.some(function (err) {
    return isGraphQLUnauthenticatedError(err, e.operation.operationName);
  }));
}));

var authLink = function authLink(authErrorCallback, apolloErrors) {
  return onError(flow(whenW(isAuthError, function () {
    return authErrorCallback(apolloErrors);
  }), whenW(function (e) {
    return typeof e !== 'undefined' && (!!e.networkError || !!e.graphQLErrors);
  }, function () {
    return apolloErrors && apolloErrors.push({
      statusCode: 500
    });
  }), constVoid));
};

var httpToWs = flow(S.replace(/^http:\/\//, 'ws://'), S.replace(/^https:\/\//, 'wss://'));
var adminOperations = ['HidePublicResponse', 'ShowPublicResponse'];

var getJwtHeader = function getJwtHeader(jwt) {
  return jwt == null ? undefined : {
    Authorization: "Bearer ".concat(jwt)
  };
};

var getCookieHeader = function getCookieHeader(cookie) {
  return cookie == null ? undefined : {
    cookie: cookie
  };
};

var constructLink = function constructLink(_ref) {
  var host = _ref.host,
      _ref$headers = _ref.headers;
  _ref$headers = _ref$headers === void 0 ? {} : _ref$headers;
  var cookie = _ref$headers.cookie,
      errorContext = _ref.errorContext,
      jwt = _ref.jwt,
      authErrorCallback = _ref.authErrorCallback;
  return function () {
    var remoteUrl = host ? attachScheme(host) : true ? window.location.origin : 'http://localhost:4751';
    return pipe(authLink(authErrorCallback, errorContext), split(function (_ref2) {
      var operationName = _ref2.operationName;
      return adminOperations.includes(operationName);
    }, new HttpLink({
      uri: remoteUrl + '/admin/graphql',
      headers: getJwtHeader(jwt) || getCookieHeader(cookie)
    })), false ? identity : split(flow(function (x) {
      return x.query;
    }, getOperationDefinition, function (definition) {
      return (definition === null || definition === void 0 ? void 0 : definition.kind) === 'OperationDefinition' && (definition === null || definition === void 0 ? void 0 : definition.operation) === 'subscription';
    }), new GraphQLWsLink(createClient({
      url: httpToWs(remoteUrl) + '/graphql',
      webSocketImpl: WS.w3cwebsocket
    }))), concat(new HttpLink({
      uri: remoteUrl + '/graphql',
      headers: getJwtHeader(jwt) || getCookieHeader(cookie)
    })));
  };
};

var createApolloClient = function createApolloClient(options) {
  return pipe(io.Do, io.apS('link', constructLink(options)), io.apS('cache', constructCache), io.apS('ssrMode', function () {
    return false;
  }), io.apS('connectToDevTools', function () {
    return false;
  }), io.chain(function (conf) {
    return function () {
      return new ApolloClient(conf);
    };
  }));
};

export var initializeApollo = function initializeApollo(options) {
  return pipe(createApolloClient(options), options.initialState ? io.chainFirst(function (client) {
    return function () {
      var existingCache = client.extract();
      client.restore(_objectSpread(_objectSpread({}, existingCache), options.initialState));
    };
  }) : identity);
};
export var useApolloWatchingJwt = function useApolloWatchingJwt(_ref3) {
  var host = _ref3.host,
      inflAuthPath = _ref3.inflAuthPath,
      _jwt = _ref3.jwt;

  var _useState = useState(_jwt),
      jwt = _useState[0],
      setJwt = _useState[1];

  useEffect(function () {
    if (host && inflAuthPath) {
      void pipe(fetchHubJwtToken(inflAuthPath), TE.chainIOK(function (token) {
        return function () {
          return setJwt(token);
        };
      }))();
    }
  }, [host, inflAuthPath, setJwt]);
  var apolloConfig = {
    authErrorCallback: constVoid
  };
  var client = useMemo(function () {
    return initializeApollo(host && jwt ? _objectSpread({
      host: host,
      jwt: jwt
    }, apolloConfig) : apolloConfig)();
  }, [host, jwt]); // Renew token every 50 minutes because token will be expired after 1 hour

  useEffect(function () {
    if (host && inflAuthPath) {
      var interval = setInterval(function () {
        return void pipe(fetchHubJwtToken(inflAuthPath), TE.chainIOK(function (jwt) {
          return constructLink(_objectSpread({
            host: host,
            jwt: jwt
          }, apolloConfig));
        }), TE.chainIOK(function (link) {
          return function () {
            return client.setLink(link);
          };
        }))();
      }, Duration.toMillis(Duration.minutes(50)));
      return function () {
        return clearInterval(interval);
      };
    }
  });
  return {
    client: client,
    jwt: jwt
  };
};